import { getModifiedStats, formatStat } from '@helpers/stats';
import { getDotNotation } from '@helpers/dotNotation';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@stores';
import * as Shared from '@shared/lib';
import { useMemo } from 'react';

interface StatsDisplayProps {
  value: string;
  compareId?: string;
  type: 'chassis' | 'weapon';
};

const prio = [
  'hp',
  'energy',
  'armor',
  'shield',
  'damage',
  'clipSize',
  'reloadDuration'
];

export function StatsDisplay({ type, value, compareId }: StatsDisplayProps) {
  const { t: tChassis } = useTranslation(undefined, { keyPrefix: 'Chassis' });
  const { t: tWeapon } = useTranslation(undefined, { keyPrefix: 'Weapon' });
  const { t } = useTranslation(undefined, { keyPrefix: 'Arsenal' });
  const { userData } = useAppStore();

  const data = type === 'chassis'
    ? Shared.chassisData.find((c) => c.key === value)
    : Shared.weaponsData.find((w) => w.key === value);

  const stats = getDotNotation(data?.stats ?? {}, '', prio);
  const compareStats = useMemo(() => {
    if (!compareId) return null;

    const finalStats = getModifiedStats(userData, compareId, type);
    return getDotNotation(finalStats, '', prio);
  }, [compareId, userData]);

  const getName = (k: string) => type === 'chassis'
    ? tChassis(`Stats.${k}`)
    : tWeapon(`Stats.${k}`);

  return (
    <>
      <p className="px-2 mt-4 text-lg">{t('stats')}</p>
      <div className="border-t border-secondary mt-1 text-sm">
        {Object.entries(stats).map(([k, v]) => (
          <div className="p-1 px-2 border-b border-secondary flex items-center justify-between bg-black/40" key={k}>
            <p className="text-gold">{getName(k)}</p>
            <div className="flex items-center">
              {compareStats && compareStats[k] !== v && (
                <p className="active mr-2 text-mono">
                  ({formatStat(k, compareStats[k])})
                </p>
              )}
              <p className="text-mono">{formatStat(k, v)}</p>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}